<template>
    <div class="base-map">
        <div class="map-container">
            <div
                class="map"
                ref="map"
            />
        </div>
    </div>
</template>

<script>
    import { Loader } from '@googlemaps/js-api-loader';
    import VueSetTimeout from '@netsells/vue-set-timeout';
    import Vue from 'vue';

    export default {
        name: 'base-map',

        mixins: [VueSetTimeout],

        props: {
            center: {
                type: Object,
                default: () => ({
                    lat: null,
                    lng: null,
                }),
            },

            markers: {
                type: Array,
                default: () => ([]),
            },

            centerMarkerIcon: {
                type: String,
                default: '',
            },

            centerMarkerPopup: {
                type: Boolean,
                default: false,
            },

            hideCenterMarker: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            /**
             * Check if a valid center has been provided.
             *
             * @returns {boolean}
             */
            hasCenter() {
                return Object.values(this.center)
                    .every(Boolean);
            },

            /**
             * Return the lat lng of the center of the map.
             * Default to london if not provided via the prop.
             *
             * @returns {object|{lng: number, lat: number}}
             */
            centerLatLng() {
                return (this.hasCenter && this.center) || {
                    lat: 51.509865,
                    lng: -0.118092,
                };
            },

            /**
             * Return the markers sorted by distance from the centre marker.
             *
             * @returns {Array}
             */
            sortedMarkers() {
                const { computeDistanceBetween } = this.google.maps.geometry.spherical;

                return this.markers
                    .concat()
                    .sort((a, b) => {
                        const aDistance = computeDistanceBetween(a, this.centerLatLng);
                        const bDistance = computeDistanceBetween(b, this.centerLatLng);

                        return aDistance > bDistance ? 1 : -1;
                    });
            },
        },

        watch: {
            // Whenever these props change we want to update them on the map
            markers: 'addMarkers',
            center: 'addCenterMarker',
        },

        mounted() {
            this.centerMarker = null;
            this.mapMarkers = [];

            this.generateMap();
        },

        methods: {
            /**
             * Load the google api.
             */
            async loadGoogleScript() {
                if (this.google) {
                    return;
                }

                // Prevent multiple setup calls
                if (window.google) {
                    this.google = window.google;
                    return;
                }

                const loader = new Loader({
                    apiKey: this.$config.GOOGLE_API_KEY,
                    version: 'weekly',
                    libraries: ['places', 'geometry'],
                });

                this.google = await loader.load();
            },

            /**
             * Generate the base map.
             */
            async generateMap() {
                await this.loadGoogleScript();

                // Lazy load the map styles
                const { default: mapStyles } = await import('../../../lib/map-styles');

                const { Map, event } = this.google.maps;

                this.map = new Map(this.$refs.map, {
                    zoom: 14,
                    center: this.centerLatLng,
                    disableDefaultUI: true,
                    zoomControl: true,
                    styles: mapStyles,
                });

                if (this.center && !this.hideCenterMarker) {
                    this.addCenterMarker();
                }

                this.addMarkers();

                event.addListenerOnce(this.map, 'idle', () => {
                    this.$el.querySelector('iframe').title = 'Google Maps';

                    this.zoomToMinimumMarkers();
                });
            },

            /**
             * Add the center marker to the map.
             */
            addCenterMarker() {
                if (!this.map) {
                    // Don't add markers until the map is ready
                    return;
                }

                if (!this.hasCenter) {
                    if (this.centerMarker) {
                        // Hide the existing center marker on the map
                        this.centerMarker
                            .setMap(null);
                    }

                    return;
                }

                if (this.centerMarker) {
                    // Update and show the existing marker
                    this.centerMarker
                        .setPosition(this.centerLatLng);
                    this.centerMarker
                        .setMap(this.map);
                    // Move the map to the new center
                    this.map
                        .panTo(this.centerLatLng);

                    return;
                }

                if (!this.hideCenterMarker) {
                    this.centerMarker = this.addMarker({
                        position: this.centerLatLng,
                        icon: {
                            url: this.centerMarkerIcon
                                || require('../../../assets/icons/svgs/map-location-marker.svg'),
                            scaledSize: {
                                width: 24,
                                height: 36,
                            },
                        },
                    });
                }

                // Move the map to the new center
                this.map
                    .panTo(this.centerLatLng);

                if (!this.centerMarkerPopup) { return; }

                // Add a popup to the center marker
                const { InfoWindow } = this.google.maps;
                this.centerMarker.infoWindow = new InfoWindow({
                    content: '<p class="h6">YOU ARE HERE</p><p>find a pink pin to locate your nearest itsu</p>',
                });

                this.centerMarker.addListener('click', () => {
                    // Close any open info windows
                    this.mapMarkers
                        .forEach(({ infoWindow }) => infoWindow?.close());

                    this.centerMarker.infoWindow.open({
                        anchor: this.centerMarker,
                        map: this.map,
                        shouldFocus: true,
                    });
                });
            },

            /**
             * Add the provided markers to the map.
             */
            addMarkers() {
                if (!this.map) {
                    // Don't add markers until the map is ready
                    return;
                }

                if (this.mapMarkers.length) {
                    // Remove existing markers
                    this.mapMarkers
                        .forEach((marker) => marker.setMap(null));
                    this.mapMarkers = [];
                }

                // Sort the markers by distance so that they
                // transition in from closest to furthest
                this.sortedMarkers.forEach((marker, index) => {
                    // Add each marker to the map
                    this.setTimeout(() => {
                        this.addItsuMarker(marker);
                    }, Math.min(50 * index, 3000));
                });
            },

            /**
             * Add an itsu marker to the map.
             *
             * @param {object} data
             */
            addItsuMarker(data) {
                const marker = this.addMarker({
                    position: {
                        lat: data.lat,
                        lng: data.lng,
                    },
                });

                const infoWindow = this.createMarkerInfoWindow(data);

                if (infoWindow) {
                    marker.infoWindow = infoWindow;

                    marker.addListener('click', () => {
                        // Close any open info windows
                        this.mapMarkers
                            .forEach(({ infoWindow }) => infoWindow?.close());
                        this.centerMarker?.infoWindow?.close();

                        infoWindow.open({
                            anchor: marker,
                            map: this.map,
                            shouldFocus: true,
                        });
                    });
                }

                this.mapMarkers.push(marker);
            },

            /**
             * Create a marker info window using a component.
             *
             * @param {object} data
             *
             * @returns {object|undefined}
             */
            createMarkerInfoWindow(data) {
                const { InfoWindow } = this.google.maps;

                const vnodes = this.$slots[`marker_info_window_${ data.id }`];

                if (!vnodes?.length) {
                    return;
                }

                this.$root.$options.$snippet = this.$snippet;

                const MarkerInfoWindow = Vue.extend({
                    $snippet: this.$snippet,
                    //eslint-disable-next-line @netsells/require-jsdoc-except/require-jsdoc
                    render() {
                        return vnodes;
                    },
                });

                const instance = new MarkerInfoWindow();

                instance.$mount(undefined, true);

                return new InfoWindow({
                    content: instance.$el.outerHTML,
                });
            },

            /**
             * Add a marker to the map.
             *
             * @param {object} payload
             * @param {object} payload.position
             * @param {object} payload.icon
             *
             * @returns {object}
             */
            addMarker({ position, icon }) {
                const { Marker, Animation } = this.google.maps;

                return new Marker({
                    position,
                    map: this.map,
                    animation: Animation.DROP,
                    icon: icon || {
                        url: require('../../../assets/icons/svgs/map-marker.svg'),
                    },
                });
            },

            /**
             * Zoom the map until x markers are visible in the viewport.
             */
            zoomToMinimumMarkers() {
                if (!this.markers.length) {
                    return;
                }

                const minimumMarkersCount = 5;
                const maxZoom = 2;

                /**
                 * Recursively zoom and count the visible markers until
                 * the count satisfies our minimumMarkersCount.
                 *
                 * @param {number} zoomLevel
                 */
                const zoomAndCheckVisible = (zoomLevel) => {
                    // Add a listener for the zoom changed so we can check the
                    // markers after the zoom has been updated
                    const zoomListener = this.map.addListener('zoom_changed', () => {
                        // Get the current viewport bounds
                        const viewport = this.map.getBounds();

                        // Check how many markers are within the bounds
                        const visibleMarkers = this.sortedMarkers.filter((item) => viewport.contains(item));

                        // Remove the event listener so it only fires once for the zoom level we are on
                        this.google.maps.event.removeListener(zoomListener);

                        // If our minimum visible marker count has not been satisfied
                        // and we have not reached the maximum zoom level, increase
                        // the zoom and check again
                        if (visibleMarkers.length < minimumMarkersCount && zoomLevel > maxZoom) {
                            zoomAndCheckVisible(zoomLevel - 1);
                        }
                    });

                    this.map.setZoom(zoomLevel);
                };

                // Check the visible markers for the initial zoom level
                zoomAndCheckVisible(this.map.getZoom());
            },
        },
    };
</script>

<style lang="scss" scoped>
    .map-container {
        position: relative;
        padding-bottom: percentage(452 / 327);
        @include media-breakpoint-up(md) {
            padding-bottom: percentage(452 / 1088);
        }
    }

    .map {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 20px;
    }

    ::v-deep {
        .gm-style .gm-style-iw-c {
            padding: 0;
        }

        .gm-style .gm-style-iw-d {
            padding: 20px;
            overflow-x: hidden !important; /* stylelint-disable-line declaration-no-important */
            scrollbar-width: 0;

            &::-webkit-scrollbar {
                width: 0;
                height: 0;
            }
        }

        .gm-ui-hover-effect {
            border: 1px solid $primary !important; /* stylelint-disable-line declaration-no-important */
            border-radius: 50%;
            display: flex !important; /* stylelint-disable-line declaration-no-important */
            align-items: center;
            justify-content: center;
            width: 20px !important; /* stylelint-disable-line declaration-no-important */
            height: 20px !important; /* stylelint-disable-line declaration-no-important */
            top: 10px !important; /* stylelint-disable-line declaration-no-important */
            right: 10px !important; /* stylelint-disable-line declaration-no-important */

            span {
                $svg: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' fill='#{ $primary }'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
                -webkit-mask-image: none !important; /* stylelint-disable-line declaration-no-important */
                background-image: escape-svg($svg);
                background-color: transparent;
                background-repeat: no-repeat;
                width: 18px !important; /* stylelint-disable-line declaration-no-important */
                height: 18px !important; /* stylelint-disable-line declaration-no-important */
                margin: 0 !important; /* stylelint-disable-line declaration-no-important */
            }
        }
    }
</style>
